/**
 * @file GoogleIntegrationSettings.tsx
 * @description Google ecosystem integration settings panel
 */

import React from 'react';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import './GoogleIntegrationSettings.css';

interface ServiceInfo {
  id: 'gmail' | 'calendar' | 'nest';
  name: string;
  icon: string;
  description: string;
}

const SERVICES: ServiceInfo[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    icon: '📧',
    description: 'Read emails, send messages, and get urgent notifications'
  },
  {
    id: 'calendar',
    name: 'Google Calendar',
    icon: '📅',
    description: 'Sync events, get reminders, and manage your schedule'
  },
  {
    id: 'nest',
    name: 'Nest Smart Home',
    icon: '🏠',
    description: 'Control thermostat, view cameras, and manage devices'
  }
];

export const GoogleIntegrationSettings: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const {
    isConfigured,
    isAuthenticated,
    connectedServices,
    loading,
    error,
    connect,
    disconnect
  } = useGoogleAuth();

  const handleConnect = () => {
    connect(['gmail', 'calendar', 'nest']);
  };

  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect Google? Your EmCog will lose access to your Gmail, Calendar, and Nest devices.')) {
      await disconnect();
    }
  };

  if (!isConfigured) {
    return (
      <div className="google-settings">
        {!embedded && (
          <div className="google-header">
            <span className="google-logo">G</span>
            <h3>Google Integration</h3>
          </div>
        )}
        <div className="google-not-configured">
          <p>Google integration is not configured on this server.</p>
          <p className="hint">
            Ask your administrator to set up Google Cloud Console credentials.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="google-settings">
      {!embedded && (
        <div className="google-header">
          <span className="google-logo">G</span>
          <h3>Google Integration</h3>
        </div>
      )}

      {error && (
        <div className="google-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="google-loading">
          <div className="loading-spinner"></div>
          <span>Checking connection...</span>
        </div>
      ) : isAuthenticated ? (
        <div className="google-connected">
          <div className="connection-status">
            <span className="status-icon">✅</span>
            <span className="status-text">Connected to Google</span>
          </div>

          <div className="connected-services">
            <h4>Connected Services</h4>
            <div className="services-grid">
              {SERVICES.map(service => {
                const isConnected = connectedServices.includes(service.id);
                return (
                  <div
                    key={service.id}
                    className={`service-card ${isConnected ? 'connected' : 'disconnected'}`}
                  >
                    <span className="service-icon">{service.icon}</span>
                    <div className="service-info">
                      <span className="service-name">{service.name}</span>
                      <span className="service-status">
                        {isConnected ? '✓ Connected' : '○ Not connected'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="google-actions">
            <button
              className="disconnect-button"
              onClick={handleDisconnect}
            >
              Disconnect Google
            </button>
          </div>

          <div className="google-capabilities">
            <h4>What your EmCog can do</h4>
            <ul>
              <li>📬 Monitor your inbox for important emails</li>
              <li>🔔 Alert you about urgent messages</li>
              <li>📅 Keep track of your upcoming events</li>
              <li>⏰ Remind you about meetings</li>
              <li>🌡️ Adjust your thermostat</li>
              <li>📹 Check security cameras</li>
              <li>🔔 Know when someone's at the door</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="google-disconnected">
          <p className="connect-intro">
            Connect your Google account to let your EmCog help with your mail and calendar.
          </p>

          <div className="services-preview">
            {SERVICES.map(service => (
              <div key={service.id} className="service-preview">
                <span className="service-icon">{service.icon}</span>
                <div className="service-details">
                  <span className="service-name">{service.name}</span>
                  <span className="service-desc">{service.description}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            className="connect-button"
            onClick={handleConnect}
          >
            <span className="google-g">G</span>
            Connect with Google
          </button>

          <p className="privacy-note">
            🔒 Your credentials are encrypted and stored securely.
            Your EmCog only accesses what's needed to help you.
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleIntegrationSettings;
